import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  FileTransferService,
  listenerTranscodingFileReplaced,
  MTMCustomFile,
  TransferType,
  UploadRequest,
} from '../../../../services/signed-url';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LogService } from '../../../../services/log.service';

export enum FailedTranscodingTypes {
  Pdf = 'pdf',
  Png = 'png',
  KeyNote = 'keynote',
}

@Component({
  selector: 'mtm-pdf-transcode-error',
  templateUrl: './pdf-transcode-error.component.html',
  styleUrl: './pdf-transcode-error.component.scss',
  standalone: false,
})
export class PdfTranscodeErrorComponent implements OnInit, OnDestroy {
  @Input() file: any;
  @Input() transcodingType: FailedTranscodingTypes = FailedTranscodingTypes.Pdf;
  @Input() forceCodec: boolean = false;
  @Output() fileReplaced: EventEmitter<any> = new EventEmitter<any>();

  private readonly transferService = inject(FileTransferService);
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private readonly logService: LogService = inject(LogService);
  ngOnInit() {
    listenerTranscodingFileReplaced.pipe(takeUntil(this.ngUnsubscribe)).subscribe((file: MTMCustomFile) => {
      if (file?.extraInfo?.failedTranscodingFileId === this.file?.id) {
        this.logService.logMessage('info', 'file replaced', { file, fileId: this.file?.id });
        this.fileReplaced.emit(file);
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  get title(): string {
    if (this.transcodingType === FailedTranscodingTypes.Pdf) {
      return 'mediaPlayer_officeTranscodingFailed_title';
    } else if (this.transcodingType === FailedTranscodingTypes.KeyNote) {
      return 'mediaPlayer_keynoteTranscodingFailed_title';
    } else {
      return 'mediaPlayer_photoshopTranscodingFailed_title';
    }
  }

  get description(): string {
    if (this.transcodingType === FailedTranscodingTypes.Pdf) {
      return 'mediaPlayer_officeTranscodingFailed_description';
    } else if (this.transcodingType === FailedTranscodingTypes.KeyNote) {
      return 'mediaPlayer_keynoteTranscodingFailed_description';
    } else {
      return 'mediaPlayer_photoshopTranscodingFailed_description';
    }
  }

  get lastStep(): string {
    if (this.transcodingType === FailedTranscodingTypes.Pdf) {
      return 'mediaPlayer_officeTranscodingFailed_lastStep';
    } else if (this.transcodingType === FailedTranscodingTypes.KeyNote) {
      return 'mediaPlayer_keynoteTranscodingFailed_lastStep';
    } else {
      return 'mediaPlayer_photoshopTranscodingFailed_lastStep';
    }
  }

  get uploadFilter(): string {
    if (
      this.transcodingType === FailedTranscodingTypes.Pdf ||
      this.transcodingType === FailedTranscodingTypes.KeyNote
    ) {
      return 'PDF_FILTER';
    } else {
      return 'PNG_FILTER';
    }
  }

  onFileSelected(files: any) {
    if (!files.files.length) {
      return;
    }

    const file = files.files[0];
    const mtmFile = new MTMCustomFile(file, 0);
    mtmFile.transferType = TransferType.TranscodingFailed;
    mtmFile.extraInfo.failedTranscodingFileId = this.file.id;
    mtmFile.extraInfo.failedTranscodingReplacementType =
      this.transcodingType === FailedTranscodingTypes.KeyNote ? FailedTranscodingTypes.Pdf : this.transcodingType;
    if (this.forceCodec) {
      mtmFile.extraInfo.failedTranscodingForceCodec = true;
    }
    const request: UploadRequest = {
      files: [mtmFile],
      uploadType: TransferType.TranscodingFailed,
    };

    this.transferService.uploadFiles(request);
  }

  protected readonly FailedTranscodingTypes = FailedTranscodingTypes;
}
